/**
 * IdeesSection — Boîte à idées côté administré.
 *
 * Liste les idées de la commune (triées par soutiens), permet d'en proposer une
 * et de soutenir (+1) celles des autres. Autonome : charge ses propres données.
 * Utilisable sur la page Consultations.
 */

import { useEffect, useState, useCallback } from 'react';
import { Card, Button, Badge, Alert, Field, Input } from '@/components/ui';
import { api, ApiException } from '@/lib/api';
import type { Idee, IdeeCategorie, IdeeStatut } from '@/lib/api';
import { Lightbulb, ThumbsUp, Plus, AlertCircle, Trash2 } from 'lucide-react';

const CATEGORIE_LABEL: Record<IdeeCategorie, string> = {
  cadre_de_vie: 'Cadre de vie', voirie: 'Voirie', environnement: 'Environnement', culture: 'Culture',
  sport: 'Sport', jeunesse: 'Jeunesse', solidarite: 'Solidarité', commerce: 'Commerce', autre: 'Autre',
};
const STATUT_LABEL: Record<IdeeStatut, string> = {
  nouvelle: 'Nouvelle', a_l_etude: 'À l\'étude', retenue: 'Retenue', realisee: 'Réalisée', refusee: 'Non retenue',
};
const STATUT_VARIANT: Record<IdeeStatut, 'neutral' | 'info' | 'success' | 'warning'> = {
  nouvelle: 'neutral', a_l_etude: 'info', retenue: 'success', realisee: 'success', refusee: 'warning',
};

export function IdeesSection() {
  const [idees, setIdees] = useState<Idee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ouvrir, setOuvrir] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try { setIdees((await api.idees.list()).idees); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Chargement impossible'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  async function soutenir(id: string) {
    // Optimiste
    setIdees((prev) => prev.map((i) => i.id === id
      ? { ...i, jeSoutiens: !i.jeSoutiens, soutiens: i.soutiens + (i.jeSoutiens ? -1 : 1) } : i));
    try { await api.idees.soutenir(id); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Action impossible'); await reload(); }
  }
  async function retirer(id: string) {
    try { await api.idees.retirer(id); await reload(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Retrait impossible'); }
  }

  return (
    <div>
      {error && <Alert variant="error" icon={AlertCircle} className="mb-3">{error}</Alert>}

      <div className="mb-3 flex items-center justify-between">
        <div className="inline-flex items-center gap-2 text-sm text-ink-muted">
          <Lightbulb className="h-4 w-4 text-grc" /> Proposez et soutenez des idées pour la commune.
        </div>
        {!ouvrir && <Button variant="primary" size="sm" onClick={() => setOuvrir(true)}><Plus className="h-4 w-4" /> Proposer</Button>}
      </div>

      {ouvrir && <ProposerForm onClose={() => setOuvrir(false)} onCreated={async () => { setOuvrir(false); await reload(); }} />}

      {loading ? (
        <p className="text-sm text-ink-muted">Chargement…</p>
      ) : idees.length === 0 ? (
        <p className="text-sm text-ink-muted">Aucune idée pour l'instant. Soyez le premier à proposer !</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {idees.map((i) => (
            <li key={i.id} className="rounded-r-lg border border-border bg-surface/50 p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 font-medium text-ink">
                    <span>{i.titre}</span>
                    <Badge variant="neutral" size="sm">{CATEGORIE_LABEL[i.categorie]}</Badge>
                    {i.statut !== 'nouvelle' && <Badge variant={STATUT_VARIANT[i.statut]} size="sm">{STATUT_LABEL[i.statut]}</Badge>}
                  </div>
                  <p className="mt-0.5 text-sm text-ink-muted">{i.description}</p>
                  {i.reponse && (
                    <p className="mt-1 rounded-r-md border-l-2 border-grc bg-grc-tint/40 px-2 py-1 text-sm text-ink">
                      <span className="font-medium">Réponse de la mairie :</span> {i.reponse}
                    </p>
                  )}
                  <div className="mt-1 text-xs text-ink-dim">Proposée par {i.auteurNom ?? 'un habitant'}</div>
                </div>
                <div className="flex flex-shrink-0 flex-col items-center gap-1">
                  <button
                    type="button" onClick={() => void soutenir(i.id)}
                    className={`inline-flex flex-col items-center gap-0.5 rounded-r-lg border px-3 py-1.5 text-sm transition-colors ${
                      i.jeSoutiens ? 'border-grc bg-grc-tint/60 text-grc-fg' : 'border-border text-ink-muted hover:border-grc/40'}`}
                    aria-pressed={i.jeSoutiens}
                    title={i.jeSoutiens ? 'Retirer mon soutien' : 'Soutenir'}
                  >
                    <ThumbsUp className="h-4 w-4" />
                    <span className="font-semibold">{i.soutiens}</span>
                  </button>
                  {i.estMonIdee && i.statut === 'nouvelle' && (
                    <button type="button" onClick={() => void retirer(i.id)} className="text-ink-dim hover:text-urgent" title="Retirer mon idée">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ProposerForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [titre, setTitre] = useState('');
  const [description, setDescription] = useState('');
  const [categorie, setCategorie] = useState<IdeeCategorie>('cadre_de_vie');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const selectCls = 'w-full rounded-r-md border border-border bg-bg px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc';

  async function envoyer() {
    setBusy(true); setErr(null);
    try { await api.idees.proposer({ titre: titre.trim(), description: description.trim(), categorie }); onCreated(); }
    catch (e) { setErr(e instanceof ApiException ? e.message : 'Envoi impossible'); setBusy(false); }
  }

  return (
    <div className="space-y-2 rounded-r-lg border border-border bg-bg-2 p-3">
      {err && <Alert variant="error" icon={AlertCircle}>{err}</Alert>}
      <Field label="Titre de l'idée" htmlFor="idee-titre" required>
        <Input id="idee-titre" value={titre} onChange={(e) => setTitre(e.target.value)} placeholder="Ex. : installer un composteur partagé" />
      </Field>
      <Field label="Catégorie" htmlFor="idee-cat">
        <select id="idee-cat" className={selectCls} value={categorie} onChange={(e) => setCategorie(e.target.value as IdeeCategorie)}>
          {Object.entries(CATEGORIE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </Field>
      <Field label="Description" htmlFor="idee-desc" required>
        <textarea id="idee-desc" rows={3}
          className="w-full rounded-r-md border border-border bg-bg px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc"
          value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder="Expliquez votre idée en quelques phrases (10 caractères minimum)." />
      </Field>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>Annuler</Button>
        <Button variant="primary" size="sm" onClick={envoyer} disabled={busy || titre.trim().length < 3 || description.trim().length < 10}>
          {busy ? 'Envoi…' : 'Publier mon idée'}
        </Button>
      </div>
    </div>
  );
}
